import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import BillToLookupField, { type BillToValue } from '../../../components/BillToLookupField';
import LookupSearchField from '../../../components/tablemaint/LookupSearchField';
import LookupResultCard from '../../order-management/components/LookupResultCard';
import quoteService from '../../../services/quote.service';
import type { CustomerLookupRow } from '../../order-management/fixtures';
import ordersService, { type ChangeTemplateCandidate } from '../../../services/orders.service';
import { ORDER_MOVE_TYPES } from '../../../constants/orderFieldOptions';
import {
  useAdditionalFieldColumns,
  useAdditionalFieldSelection,
} from '../../../hooks/useAdditionalFields';

interface CodeName {
  recno: number;
  name: string;
}

export interface CreateOrderTemplatePickerPrefill {
  billTo: CodeName;
  customer: CodeName;
  movetype: string;
  delivertoName: string;
}

interface Props {
  open: boolean;
  /** Set while the parent is fetching the picked template's details — disables
   *  row clicks and shows a small overlay so a double-pick can't fire twice. */
  busy?: boolean;
  prefill: CreateOrderTemplatePickerPrefill;
  onPick: (templatePro: string) => void;
  onBack: () => void;
  onClose: () => void;
}

const s = (v: unknown): string => (v == null ? '' : String(v));

/** Candidate grid columns, also offered by the table's Add column picker.
 *  `pronumber` opts out of registry titling — the registry calls the field
 *  "Pro No." and these rows are TEMPLATES. De-duplication is unaffected, so it is
 *  still not offered a second time in the picker. */
const COLUMNS: ColumnDef<ChangeTemplateCandidate>[] = [
  { accessorKey: 'pronumber', header: 'Template #', meta: { registryTitle: false }, cell: (c) => s(c.getValue()) },
  { accessorKey: 'movetype', header: 'Move', cell: (c) => s(c.getValue()) },
  { accessorKey: 'ratezone', header: 'Rate Zone', cell: (c) => s(c.getValue()) },
  { accessorKey: 'size', header: 'Size', cell: (c) => s(c.getValue()) },
  { accessorKey: 'equiptype', header: 'Type', cell: (c) => s(c.getValue()) },
  { accessorKey: 'isdrop', header: 'Drop', cell: (c) => s(c.getValue()) },
  { accessorKey: 'pickup', header: 'Pick Up', cell: (c) => s(c.getValue()) },
  { accessorKey: 'delivery', header: 'Delivery', cell: (c) => s(c.getValue()) },
  { accessorKey: 'quotenumber', header: 'Quote #', cell: (c) => s(c.getValue()) },
  { accessorKey: 'csr', header: 'CSR', cell: (c) => s(c.getValue()) },
  { accessorKey: 'approvedby', header: 'Approved By', cell: (c) => s(c.getValue()) },
];

/** Namespaces this grid's saved layout AND its added-column selection. */
const CANDIDATE_GRID_KEY = 'create-order-template-picker';

/**
 * Create New Order — Step "picker" (design doc §3). Thin copy of
 * ChangeTemplateModal's step-1 search block: Bill To / Customer stay
 * EDITABLE (looser than V1, matching the shipped Change Template picker),
 * plus a user-adjustable Move Type filter and a free-text Deliver To filter.
 *
 * Deliver To is deliberately a free-text input forwarded as the
 * `deliverto_name` param (V1 changetemplate_results: `db.lname LIKE '%x%'`) —
 * NOT the recno `LocationLookupField` ChangeTemplateModal uses. That lookup
 * needs a resolved location record; the create flow only has whatever the
 * user typed into the modal's "Deliver To Filter" text field, so this picker
 * keeps it as text rather than forcing a second lookup round-trip.
 */
const CreateOrderTemplatePicker: React.FC<Props> = ({
  open,
  busy,
  prefill,
  onPick,
  onBack,
  onClose,
}) => {
  const [billTo, setBillTo] = useState<BillToValue>({ recno: 0, name: '' });
  const [customer, setCustomer] = useState<CodeName>({ recno: 0, name: '' });
  const [movetype, setMovetype] = useState('');
  const [deliverToName, setDeliverToName] = useState('');
  const [rows, setRows] = useState<ChangeTemplateCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [capped, setCapped] = useState(false);

  // Full re-seed on every open — a stale search from a previous modal pass
  // must never leak into the next one.
  useEffect(() => {
    if (!open) return;
    setBillTo({ recno: prefill.billTo.recno || 0, name: prefill.billTo.name || '' });
    setCustomer({ recno: prefill.customer.recno || 0, name: prefill.customer.name || '' });
    setMovetype(prefill.movetype || '');
    setDeliverToName(prefill.delivertoName || '');
  }, [open, prefill.billTo.recno, prefill.billTo.name, prefill.customer.recno, prefill.customer.name, prefill.movetype, prefill.delivertoName]);

  const canSearch = open && billTo.recno > 0 && customer.recno > 0;

  // Any orders-scope registry field can be added as a column, resolved against the
  // TEMPLATE tables — the same picker the Templates board offers. Keyed on `open`:
  // this picker stays MOUNTED inside the create flow's provider, and a null key
  // means "no grid to remember for", so a session that never opens it pays no
  // settings round-trip.
  const fieldSelection = useAdditionalFieldSelection(
    open ? CANDIDATE_GRID_KEY : null, { binding: 'templates' },
  );
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const { columns, availableFields } = useAdditionalFieldColumns<ChangeTemplateCandidate>(
    fieldSelection, { baseColumns: COLUMNS, binding: 'templates' },
  );

  // Debounced live search once both bill-to + customer are set (V1 parity —
  // ChangeTemplateModal's L153-183 pattern).
  useEffect(() => {
    // Holding on `ready` keeps the first search from firing WITHOUT the saved added
    // columns and then again with them.
    if (!canSearch || !fieldSelection.ready) {
      setRows([]);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      ordersService
        .changeTemplateCandidates(
          {
            billto: billTo.recno,
            customer: customer.recno,
            movetype: movetype.trim() !== '' ? movetype : undefined,
            deliverto_name: deliverToName.trim() !== '' ? deliverToName.trim() : undefined,
            additionalFields: fieldSelection.additionalFields,
          },
          ctrl.signal,
        )
        .then((res) => {
          setRows(res.rows);
          setCapped(res.capped);
        })
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
    // `additionalFieldsKey` (not the array) so a re-render with an equal selection
    // doesn't re-run the search; adding or removing a column does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSearch, billTo.recno, customer.recno, movetype, deliverToName,
      fieldSelection.ready, additionalFieldsKey]);

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-scrim/40 p-4"
      onMouseDown={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-[min(1040px,96vw)] max-h-[90vh] flex flex-col"
        role="dialog"
        aria-label="Pick a Template"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title="Pick a Template"
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          className="shrink-0"
        />

        <div className="p-4 flex flex-col gap-4 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <BillToLookupField value={billTo} onChange={setBillTo} required />
            <div>
              <LookupSearchField<CustomerLookupRow>
                label="Customer"
                required
                displayValue={customer.name}
                onType={(t) => setCustomer({ recno: 0, name: t })}
                onSelect={(r) => setCustomer({ recno: r.customercode, name: r.custname })}
                search={(q) => quoteService.lookupCustomer(q)}
                renderItem={(r) => (
                  <LookupResultCard
                    name={r.custname}
                    code={r.customercode}
                    address1={r.address1}
                    address2={r.address2}
                    city={r.city}
                    state={r.state}
                    zip={r.zip}
                  />
                )}
                itemValue={(r) => String(r.customercode)}
                itemLabel={(r) => r.custname}
                placeholder="Search customers by name or #…"
              />
              <div className="mt-1.5 text-xs">
                {customer.recno > 0 ? (
                  <span className="text-green-700 dark:text-green-400">#{customer.recno}</span>
                ) : (
                  <span className="text-muted">No Customer selected</span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-label mb-1" htmlFor="picker-movetype">
                Move Type
              </label>
              <select
                id="picker-movetype"
                aria-label="Move Type"
                className="w-full border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                value={movetype}
                onChange={(e) => setMovetype(e.target.value)}
              >
                <option value="">— All —</option>
                {ORDER_MOVE_TYPES.map((mt) => (
                  <option key={mt} value={mt}>{mt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-label mb-1" htmlFor="picker-deliverto">
                Deliver To Filter
              </label>
              <input
                id="picker-deliverto"
                type="text"
                aria-label="Deliver To Filter"
                className="w-full border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-[0.75rem] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                value={deliverToName}
                onChange={(e) => setDeliverToName(e.target.value)}
                onBlur={(e) => setDeliverToName(e.target.value.toUpperCase())}
                placeholder="Optional — narrow by delivery location name…"
              />
            </div>
          </div>

          <div className="relative flex-1 min-h-[320px] overflow-hidden flex flex-col">
            {!canSearch ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-500 dark:text-gray-400">
                Select a Bill To and Customer to find matching approved templates.
              </div>
            ) : (
              <>
                <TanTable<ChangeTemplateCandidate>
                  data={rows}
                  columns={columns}
                  settingsKey={CANDIDATE_GRID_KEY}
                  availableFields={availableFields}
                  additionalFields={fieldSelection.additionalFields}
                  onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
                  loading={loading}
                  enableSearch
                  searchPlaceholder="Filter templates…"
                  enablePagination={false}
                  getRowId={(r) => String(r.pronumber)}
                  onRowClick={(r) => {
                    if (busy) return;
                    onPick(String(r.pronumber));
                  }}
                />
                {capped && (
                  <div className="mt-1 text-[0.6875rem] text-amber-600 dark:text-amber-400">
                    Showing the first results — narrow the search to see more.
                  </div>
                )}
                {busy && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-800/60">
                    <span className="text-sm text-slate-600 dark:text-gray-300">Loading template…</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex justify-start shrink-0">
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            className="px-3 py-1.5 rounded border border-slate-300 dark:border-gray-600 text-sm hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CreateOrderTemplatePicker;
